import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ContrastMode = 'default' | 'dark' | 'high-contrast';

const A11Y_STORAGE_KEY = 'a11y_preferences';
const MIN_SCALE = 0.8;
const MAX_SCALE = 1.2;
const SCALE_STEP = 0.1;
const DEFAULT_SCALE = 1;

interface A11yPreferences {
  fontScale: number;
  contrastMode: ContrastMode;
  reducedMotion: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AccessibilityService {
  private readonly renderer: Renderer2;

  private readonly fontScaleSubject = new BehaviorSubject<number>(
    DEFAULT_SCALE
  );
  private readonly contrastModeSubject = new BehaviorSubject<ContrastMode>(
    'default'
  );
  private readonly reducedMotionSubject = new BehaviorSubject<boolean>(false);

  readonly fontScale$ = this.fontScaleSubject.asObservable();
  readonly contrastMode$ = this.contrastModeSubject.asObservable();
  readonly reducedMotion$ = this.reducedMotionSubject.asObservable();

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.loadPreferences();
  }

  get currentFontScale(): number {
    return this.fontScaleSubject.value;
  }

  get currentContrastMode(): ContrastMode {
    return this.contrastModeSubject.value;
  }

  get currentReducedMotion(): boolean {
    return this.reducedMotionSubject.value;
  }

  increaseFontScale(): void {
    const newScale = Math.min(
      MAX_SCALE,
      this.roundScale(this.fontScaleSubject.value + SCALE_STEP)
    );
    this.setFontScale(newScale);
  }

  decreaseFontScale(): void {
    const newScale = Math.max(
      MIN_SCALE,
      this.roundScale(this.fontScaleSubject.value - SCALE_STEP)
    );
    this.setFontScale(newScale);
  }

  resetFontScale(): void {
    this.setFontScale(DEFAULT_SCALE);
  }

  setContrastMode(mode: ContrastMode): void {
    this.removeContrastClasses();
    if (mode !== 'default') {
      this.renderer.addClass(document.documentElement, mode);
    }
    this.contrastModeSubject.next(mode);
    this.savePreferences();
  }

  toggleContrastMode(): void {
    const modes: ContrastMode[] = ['default', 'dark', 'high-contrast'];
    const currentIndex = modes.indexOf(this.contrastModeSubject.value);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    this.setContrastMode(nextMode);
  }

  toggleReducedMotion(): void {
    const newValue = !this.reducedMotionSubject.value;
    this.reducedMotionSubject.next(newValue);
    if (newValue) {
      this.renderer.addClass(document.documentElement, 'a11y-reduced-motion');
    } else {
      this.renderer.removeClass(
        document.documentElement,
        'a11y-reduced-motion'
      );
    }
    this.savePreferences();
  }

  fixMissingAltText(): number {
    let count = 0;
    const images = document.querySelectorAll('img:not([alt])');
    images.forEach(img => {
      img.setAttribute('alt', 'image');
      count++;
    });

    const svgIcons = document.querySelectorAll('svg-icon:not([aria-hidden])');
    svgIcons.forEach(icon => {
      if (!icon.getAttribute('aria-label') && !icon.getAttribute('title')) {
        icon.setAttribute('aria-hidden', 'true');
        count++;
      }
    });

    return count;
  }

  getFontScalePercent(): number {
    return Math.round(this.fontScaleSubject.value * 100);
  }

  private setFontScale(scale: number): void {
    document.documentElement.style.setProperty(
      '--a11y-font-scale',
      String(scale)
    );
    if (scale === DEFAULT_SCALE) {
      this.renderer.removeClass(document.documentElement, 'a11y-font-scaled');
    } else {
      this.renderer.addClass(document.documentElement, 'a11y-font-scaled');
    }
    this.fontScaleSubject.next(scale);
    this.savePreferences();
  }

  private roundScale(value: number): number {
    return Math.round(value * 10) / 10;
  }

  private removeContrastClasses(): void {
    this.renderer.removeClass(document.documentElement, 'dark');
    this.renderer.removeClass(document.documentElement, 'high-contrast');
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem(A11Y_STORAGE_KEY);
      if (stored) {
        const prefs: A11yPreferences = JSON.parse(stored);

        if (prefs.fontScale >= MIN_SCALE && prefs.fontScale <= MAX_SCALE) {
          this.setFontScale(prefs.fontScale);
        }

        if (['default', 'dark', 'high-contrast'].includes(prefs.contrastMode)) {
          this.setContrastMode(prefs.contrastMode);
        }

        if (prefs.reducedMotion) {
          this.reducedMotionSubject.next(true);
          this.renderer.addClass(
            document.documentElement,
            'a11y-reduced-motion'
          );
        }
      }
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }

  private savePreferences(): void {
    try {
      const prefs: A11yPreferences = {
        fontScale: this.fontScaleSubject.value,
        contrastMode: this.contrastModeSubject.value,
        reducedMotion: this.reducedMotionSubject.value,
      };
      localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }
}
